import { useMemo } from "react";

import { formatInTimeZone } from "date-fns-tz";

import { CoachAvailabilityResponse } from "../../shared/types/CoachAvailabilityResponse";
import { useBookingContext } from "../context/BookingContext";
import { dateTimeFormats } from "../conts/dateTimeFormats";

type UseTimezonedSpotsProps = {
  coachAvailability: CoachAvailabilityResponse;
};

/**
 * Custom hook to get timezoned time slots for a coach's availability.
 * It transforms the coach's availability data from the API
 * which is an element with the following shape:
 * [{
 *  "date": "2026-02-15",
 *  "status": "unavailable",
 *  "spots": []
 * },
 * {
 *  "date": "2026-02-16",
 *  "status": "available",
 *  "spots": [
 *    {
 *      "start": "2026-02-16T09:00:00Z",
 *      "end": "2026-02-16T09:30:00Z"
 *    },
 *    {
 *      "start": "2026-02-16T09:15:00Z",
 *      "end": "2026-02-16T09:45:00Z"
 *    },
 *  ]
 * }]
 *
 * and transforms it into an object with this other shape
 *
 * {
 *   "2026-01-31": [ "2026-01-31T19:00:00-05:00", "2026-01-31T19:15:00-05:00" ],
 *   "2026-02-01": [ "2026-02-01T00:00:00-05:00", "2026-02-01T00:15:00-05:00" ]
 * }
 *
 * while applying the selected timezone to all timeslots.
 *
 * @param coachAvailability - The coach's availability data.
 * @returns A record of timezoned time slots.
 */

export const useTimezonedSpots = ({
  coachAvailability,
}: UseTimezonedSpotsProps) => {
  const { timezone = { label: "UTC", value: "UTC" } } = useBookingContext();

  const timezonedSpots = useMemo<Record<string, string[]>>(() => {
    const timeSpots = coachAvailability.reduce<Record<string, string[]>>(
      (acc, day) => {
        // skip days with no spots
        if (!day.spots) {
          return { ...acc };
        }

        // if a day has spots map over them applying the selected timezone
        const daySpots = day.spots?.map((spot) => {
          return formatInTimeZone(
            spot.start,
            timezone.value,
            dateTimeFormats.dateWithTz,
          );
        });

        daySpots.forEach((spot) => {
          // for each spot, create a key based on its date
          const key = formatInTimeZone(
            spot,
            timezone.value,
            dateTimeFormats.yearMonthDay,
          );

          // create a new entry for the date if it doesn't exist
          // or push it to an existing entry
          if (!acc[key]) {
            acc[key] = [spot];
          } else if (!acc[key].includes(spot)) {
            acc[key].push(spot);
          }
        });

        return acc;
      },
      {},
    );

    return timeSpots;
  }, [coachAvailability, timezone]);

  return timezonedSpots;
};

/**
 * CONSIDERATIONS ABOUT THIS HOOK
 * - The hook recalculates the timezoned spots whenever the coachAvailability or timezone changes.
 *   each run should be in order of O(n*m) where n is the number of days and m is the number of spots per day.
 *   which can be pretty bad for large datesets but in our domain we are dealing with 1 month at time for a real person availability and this is fine
 *   we may consider changing it to a more efficient algorithm if needed.
 *
 * - The hook uses date-fns-tz for timezone conversion. Ensure that the library is up-to-date and covers all edge cases.
 */
